// ═══════════════════════════════════════════════════════════════════════════
//  BRAND CONFIG  —  "build once, brand many"
// ═══════════════════════════════════════════════════════════════════════════
//
//  This is the ONLY file you edit to spin up a new branded website.
//  Everything brand-specific (name, colors, slogan, service area, wording)
//  lives here. The app reads from the ACTIVE brand below.
//
//  ── To switch which brand the site shows ──────────────────────────────────
//  Option A (quick): change ACTIVE_BRAND below to another key in `brands`.
//  Option B (per deploy): set the environment variable REACT_APP_BRAND
//           (e.g. REACT_APP_BRAND=quickpropertyservices) — this wins over
//           ACTIVE_BRAND, so the SAME code can deploy as different brands.
//
//  ── To add a NEW brand ────────────────────────────────────────────────────
//  Copy one of the blocks in `brands`, give it a new key, and change the
//  name / colors / slogans / service area. That's it — no other code changes.
// ═══════════════════════════════════════════════════════════════════════════

// Default brand if no REACT_APP_BRAND env var is set.
const ACTIVE_BRAND = 'quickpropertyservices';

const brands = {
  // ─────────────────────────────────────────────────────────────────────────
  //  QUICK PROPERTY SERVICES
  // ─────────────────────────────────────────────────────────────────────────
  quickpropertyservices: {
    key: 'quickpropertyservices',

    name: { primary: 'Quick Property', accent: 'Services', full: 'Quick Property Services' },

    location: 'Serving Utah County & Beyond',

    serviceArea: {
      regionLabel: 'North & South Utah County',
      primaryCities: ['Alpine', 'Cedar Hills', 'Highland', 'Lehi', 'Draper', 'Suncrest', 'Pleasant Grove', 'Lindon'],
    },

    hero: {
      headingTop: 'Reliable Property Care',
      headingEm: 'Done Right, On Time',
      blurbLead: 'Quick Property Services',
      blurbRest:
        ' handles it all — repairs, roofing, siding, storm damage, and remodels. Fast, professional, and built on trust.',
      ctaLabel: 'Submit a Service Request',
    },

    stats: [
      { big: 'Fast', small: 'Response' },
      { big: '100%', small: 'Satisfaction' },
      { big: '★ 5.0', small: 'Avg Rating' },
    ],

    steps: [
      {
        title: 'Submit a Service Request',
        body: 'Tell us what you need and add photos or videos — it takes less than two minutes.',
      },
      {
        title: 'We Take It From There',
        body: 'Our team reviews your request and reaches out the same day with next steps.',
      },
      {
        title: 'We Get It Done',
        body: 'Professional crews complete the work on schedule, with progress updates along the way.',
      },
    ],

    slogan: 'Your property, our priority.',
    copyrightYear: 2017,
    adminEmailPlaceholder: 'admin@quickpropertyservices.com',

    // ── What goes on a bill ──────────────────────────────────────────────
    // The letterhead of an invoice: the name that is actually on the company,
    // the number they ring about it, the address they reply to, and when it
    // is due. Kept here with the rest of the brand rather than in the printed
    // document, so a second brand's invoices carry that brand's details.
    company: {
      legalName: 'Quick Property Services LLC',
      phone: '(801) 613-0482',
      billingEmail: 'Billing@quickpropertyservices.com',
      paymentTerms: 'Due upon receipt',
    },

    // ─────────────────────────────────────────────────────────────────────────
    //  PUBLIC LANDING PAGE  (screens/Home.js)
    //
    //  Every word and every photo on the marketing homepage lives here, so the
    //  page itself is layout only. Change the copy below and the site changes.
    //
    //  ── PHOTOS ──────────────────────────────────────────────────────────────
    //  Only OUR OWN finished work goes in here — never stock photography,
    //  which is both a licensing problem and a lie about whose work it is.
    //  All six are real jobs, served from client/public/work/. Any slot left
    //  empty draws a quiet forest/olive panel rather than a broken image, so
    //  the page stays presentable while a photo is being swapped. See the
    //  note on `images` at the bottom of `site` before adding one.
    // ─────────────────────────────────────────────────────────────────────────
    site: {
      // Top-left lockup: the seal, a hairline rule, then these two lines. The
      // mark itself is components/Seal.js, not a string — the same drawing the
      // portals and the printed work orders use.
      wordmark: { line1: 'Quick Property', line2: 'Services' },

      nav: { services: 'Services', work: 'Our Work', about: 'About', reviews: 'Reviews', cta: 'Request a Consultation' },

      // Where the REVIEWS nav link goes — the Google Business Profile. The link
      // is hidden entirely while this is empty rather than pointing nowhere.
      reviewsUrl: '',

      banner: {
        // The headline breaks after "Expect" so the italic line lands alone.
        headline: 'Professional Property Care for Homeowners Who Expect',
        headlineEm: 'Exceptional Workmanship.',
        blurb:
          'Repairs, remodeling, and ongoing home care delivered with clear communication, meticulous craftsmanship, and respect for your home.',
        cta: 'Schedule a Consultation',
        trustLine: 'Serving Utah County & Salt Lake County since 2017',
      },

      standard: {
        title: 'The QPS Standard™',
        // `icon` picks one of the line drawings in components/StandardIcons.js.
        pillars: [
          { icon: 'uniform', title: 'Professional Appearance', body: 'Uniformed team, clean trucks, and a professional presence on every job.' },
          { icon: 'message', title: 'Clear Communication', body: "You'll always know what's happening, when we'll be there, and what's next." },
          { icon: 'house', title: 'Respect for Your Home', body: 'We treat your home like our own—protecting your space and cleaning up thoroughly.' },
          { icon: 'hammer', title: 'Quality Workmanship', body: 'Meticulous craftsmanship and attention to detail that lasts.' },
        ],
      },

      services: {
        title: 'What We Do',
        // Kept in step with SERVICE_TYPES in screens/RequestForm.js — the list a
        // customer actually picks from when they send the request.
        items: [
          { name: 'General Repair', body: 'The running list every house builds up—doors, trim, fixtures, the things that never quite got done.' },
          { name: 'Carpentry', body: 'Built-ins, shelving, stairs, and finish work measured and fitted to the room it lives in.' },
          { name: 'Drywall & Paint', body: 'Patches that disappear and finishes that hold up, from one wall to the whole floor.' },
          { name: 'Kitchens & Baths', body: 'Full remodels run end to end, with one point of contact and a schedule you can plan around.' },
          { name: 'Basement Finishing', body: 'Unfinished square footage turned into rooms your family actually uses.' },
          { name: 'Fence & Deck', body: 'Outdoor structures built and repaired to stand up to a Utah winter.' },
        ],
      },

      work: { title: 'Featured Projects', cta: 'View More Projects' },

      process: {
        title: 'Our Process',
        steps: [
          { label: 'Consultation', body: 'We listen, assess your needs, and provide expert recommendations.' },
          { label: 'Planning', body: 'We create a clear plan with transparent communication.' },
          { label: 'Preparation', body: 'We prepare your space carefully to ensure a smooth, clean process.' },
          { label: 'Craftsmanship', body: 'Our team delivers meticulous work with pride and attention to detail.' },
          { label: 'Final Walkthrough', body: 'We review every detail with you to ensure your complete satisfaction.' },
        ],
      },

      closing: {
        line1: 'Experience the QPS Standard.',
        line2: "Let's take care of your home.",
        cta: 'Request a Consultation Today',
      },

      // See the note above. `projects` draws one tile per entry; four is what
      // the layout is designed around.
      //
      // Every slot is `{ src, alt }`. These are real QPS jobs, shot on site and
      // cropped to the panel each one fills. They are served from
      // client/public/work/ rather than Cloudinary: they are the only images on
      // the public page's critical path, and shipping them with the build means
      // they can never 404 on someone else's account. They are deliberately NOT
      // in the service worker's cache-first list (that is /static/* only, which
      // is content-hashed) — an unhashed filename cached forever would pin an
      // old photo the day it gets replaced.
      //
      // `alt` is not optional dressing: the page paints these as CSS background
      // images, so without it six of the largest things on the homepage are
      // invisible to a screen reader and to Google. Describe the work, never the
      // client — no names, no addresses, nothing that identifies whose house it
      // is. Source files are stripped of EXIF (GPS included) before they land
      // here; if you add one by hand, strip it first.
      //
      // A slot left empty still draws its designed forest/olive panel, so the
      // page survives a photo being pulled.
      images: {
        banner: {
          src: '/work/home-hero.jpg',
          alt: 'A home library fitted wall to wall with painted built-in shelving over a walnut counter',
        },
        closing: {
          src: '/work/home-closing.jpg',
          alt: 'Cedar shake siding and a stained timber bracket under a finished gable',
        },
        projects: [
          { src: '/work/work-patio.jpg',    alt: 'A covered patio framed in timber, its ceiling clad in cedar plank, looking out on trees' },
          { src: '/work/work-pantry.jpg',   alt: 'A butler’s pantry with lit walnut shelves, a stone counter and painted cabinets' },
          { src: '/work/work-detail.jpg',   alt: 'A brass pull on a painted cabinet drawer beneath a stone countertop' },
          { src: '/work/work-builtins.jpg', alt: 'A built-in cabinet wall with painted shelving over a walnut counter and a beadboard back' },
        ],
      },
    },

    // Olive & Oat palette: forest, olive, sage mist, oat, camel accent.
    theme: {
      '--blue': '#A8804C',        // primary accent — camel (buttons, links, active states)
      '--blue-dark': '#8A683A',   // hover / darker camel
      '--blue-light': '#EDE5D6',  // pale camel tint (badges, backgrounds)
      '--navy': '#2C3128',        // forest — dark nav bar & headings
      '--nav-accent': '#C6B08A',  // warm camel accent on the forest nav
    },
  },
};

// ── Resolve the active brand ────────────────────────────────────────────────
// An env var (REACT_APP_BRAND) wins so the same code can deploy as any brand.
const selectedKey =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BRAND) ||
  ACTIVE_BRAND;

const brand = brands[selectedKey] || brands[ACTIVE_BRAND];

// ── Apply the brand's theme colors to the page ──────────────────────────────
// Call this once when the app loads. It writes each theme color into the
// document's CSS variables, re-skinning everything that uses them.
export function applyBrandTheme() {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  Object.entries(brand.theme).forEach(([varName, value]) => {
    root.style.setProperty(varName, value);
  });
  document.title = brand.name.full;
}

export { brands };
export default brand;
